import { ContentAdd, ContentDelete } from '@fs/zion-icon'
import {
  Button,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Divider,
  IconButton,
  InputGroup,
  ResponsiveTableRow,
  Row,
  Select,
  TextField,
  useOverlay,
  H5,
  H6,
} from '@fs/zion-ui'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { DmcResponsiveTable } from '../../../common/DmcResponsiveTable'
import { getLastPathElement, printValueOrBooleanReadable } from '../../../../util/StringUtil'
import { DmcConclusionFactUIOnly } from '../../../common/conclusionFact/DmcConclusionFactUIOnly'
import DmcDataView from '../../../common/DmcDataView'
import { DmcDatePicker } from '../../../common/DmcDatePicker/DmcDatePicker'
import DmcPlaceStandards from '../../../common/DmcPlaceStandards'
import DmcTemplePicker from '../../../common/DmcTemplePicker'
import { RawData } from '../../../common/RawData'
import { makeNewEmptyConclusion } from '../../PersonUtils'
import { useCreateCtrController } from './useCreateCtrController'

export default function CreateCtrButton({ currentPerson, refetchOrdinances }) {
  const { t } = useTranslation()
  const createCtrDialogOpener = useOverlay()

  const { createCtrState, createCtrActions } = useCreateCtrController(currentPerson, refetchOrdinances)

  const cancelBtnOnClick = () => {
    createCtrDialogOpener.close()
  }
  const submitListBtnOnClick = () => {
    createCtrDialogOpener.close()
    createCtrActions.submitList()
  }

  return (
    <>
      <Button id="createCtrBtn" emphasis="medium" onClick={createCtrDialogOpener.handleClick}>
        {t('ordinances.createCtr.createCtr', 'Create CTR')}
      </Button>
      <DialogOverlay
        data-testid="printFamilyOrdinanceCardsDialogTestId"
        {...createCtrDialogOpener}
        title={t('ordinances.recordAMissingOrdinance', 'Record a Missing Ordinance')}
        size="xxl"
      >
        <OrdinanceList createCtrState={createCtrState} createCtrActions={createCtrActions} />
        <br />
        <NewOrdinance createCtrState={createCtrState} createCtrActions={createCtrActions} />
        <Person person={createCtrState.principal} createCtrActions={createCtrActions} />
        {getLastPathElement(createCtrState.ordinance.type) === 'SEALING_TO_PARENTS' && (
          <>
            <Person person={createCtrState.father} createCtrActions={createCtrActions} />

            <Person person={createCtrState.mother} createCtrActions={createCtrActions} />
          </>
        )}
        {getLastPathElement(createCtrState.ordinance.type) === 'SEALING_TO_SPOUSE' && (
          <>
            <MarriageDetails createCtrState={createCtrState} createCtrActions={createCtrActions} />

            <Person person={createCtrState.spouse} createCtrActions={createCtrActions} />
          </>
        )}
        <RawData state={createCtrState} testId="createCtrDialogState" viewJson />

        <DialogOverlayFooter>
          <DialogOverlayActions>
            <Button
              id="submitListBtn"
              emphasis="high"
              disabled={createCtrState.ordinances.length === 0}
              onClick={submitListBtnOnClick}
            >
              {t('ordinances.buttons.submitList', 'Submit List')}
            </Button>
            <Button emphasis="medium" onClick={cancelBtnOnClick}>
              {t('action.cancel', 'Cancel')}
            </Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      </DialogOverlay>
    </>
  )
}

function OrdinanceList({ createCtrState, createCtrActions }) {
  const { t } = useTranslation()
  return (
    <>
      <Row>
        <H5 growX>{t('ordinances.headers.ordinanceList', 'Ordinance List')}</H5>
        <Button
          disabled={createCtrState.ordinances.length === 0}
          emphasis="high"
          onClick={() => createCtrActions.clearOrdinancesList()}
        >
          {t('ordinances.buttons.clearList', 'Clear List')}
        </Button>
      </Row>
      {createCtrState.ordinances.length > 0 && (
        <>
          <DmcResponsiveTable
            totalColumnCount={4}
            atSizeColumns={{
              default: {
                labels: [
                  t('type', 'Type'),
                  t('ordinances.headers.performedDate', 'Performed Date'),
                  t('temple.label', 'Temple'),
                  t('principal', 'Principal'),
                ],
              },
            }}
          >
            {createCtrState.ordinances.map((ordinance) => {
              return (
                <ResponsiveTableRow
                  key={ordinance.type}
                  column1={getLastPathElement(ordinance.type)}
                  column2={ordinance.performedDate?.normalized?.[0]?.value || ordinance.performedDate?.original}
                  column3={ordinance.temple}
                  column4={ordinance.principal.name}
                />
              )
            })}
          </DmcResponsiveTable>
          <Divider />
        </>
      )}
    </>
  )
}

function NewOrdinance({ createCtrState, createCtrActions }) {
  const { t } = useTranslation()

  function setTheTemple(selection) {
    if (selection) {
      createCtrActions.setTemple(selection.templeCode)
    }
  }

  return (
    <>
      <Row>
        <H5 growX>{t('ordinances.newOrdinance', 'New Ordinance')}</H5>
        <Button
          id="addToListBtn"
          disabled={
            !(
              createCtrState?.ordinance?.type &&
              createCtrState?.ordinance?.temple &&
              (createCtrState?.ordinance?.dateStandards?.original ||
                createCtrState?.ordinance?.dateStandards?.primaryText)
            )
          }
          emphasis="high"
          onClick={createCtrActions.addOrdinanceToList}
        >
          {t('ordinances.buttons.addToList', 'Add to List')}
        </Button>
      </Row>
      <Row>
        <Select
          id="ctrType"
          label={t('type', 'Type')}
          placeholder={t('odm.selectType', 'Select Type')}
          value={createCtrState.ordinance.type}
          onChange={(event) => createCtrActions.setOrdinanceType(event.target.value)}
          options={createCtrState.controls.ordinanceTypes}
        />
        <DmcTemplePicker onSuggestionSelected={setTheTemple} onClear={() => createCtrActions.setTemple()} growX fillX />
        <DmcDatePicker
          idType="performed"
          label={t('ordinances.headers.performedDate', 'Performed Date')}
          key="newOrdinance_date"
          selectedItem={createCtrState.ordinance.dateStandards}
          setSelectedItem={(date) => createCtrActions.setOrdinanceDateStandards(date)}
        />
      </Row>
    </>
  )
}

function MarriageDetails({ createCtrState, createCtrActions }) {
  const { t } = useTranslation()

  return (
    <>
      <Divider />
      <H5>{t('marriage', 'Marriage')}</H5>
      <Row>
        <DmcDatePicker
          key="marriageDetails_date"
          selectedItem={createCtrState.marriage.dateStandards}
          setSelectedItem={(e) => {
            createCtrActions.setMarriageDateStandards(e)
          }}
        />
        <DmcPlaceStandards
          selectedItem={createCtrState.marriage.placeStandards}
          setSelectedItem={(place) => createCtrActions.setMarriageDateStandards(place)}
        />
      </Row>
    </>
  )
}

function Person({ person, createCtrActions }) {
  return (
    <>
      <PersonDetails person={person} createCtrActions={createCtrActions} />
      <Name person={person} createCtrActions={createCtrActions} />
      <Facts person={person} createCtrActions={createCtrActions} />
      <br />
    </>
  )
}

function PersonDetails({ person, createCtrActions }) {
  const { t } = useTranslation()

  const personData = [
    {
      label: <b>{t('personId', 'Person ID')}</b>,
      detail:
        person.heading === t('principal', 'Principal') ? (
          person.id
        ) : (
          <>
            <div style={{ display: 'inline-block' }}>
              <TextField
                type="text"
                value={person.id}
                onChange={(event) => createCtrActions.setPID(person.heading, event.target.value)}
              />
            </div>
            <Button emphasis="medium" onClick={() => createCtrActions.readNewPID(person.heading, person.id)}>
              {t('ordinances.buttons.readNewPid', 'Read New PID')}
            </Button>
          </>
        ),
      key: person.heading,
    },
    {
      label: <b>{t('living', 'Living')}</b>,
      detail: printValueOrBooleanReadable(person.living),
      key: `${person.heading}-living`,
    },
    {
      label: <b>{t('gender.label', 'Gender')}</b>,
      detail: person.gender,
      key: `${person.heading}-gender`,
    },
  ]

  return (
    <>
      <Divider />
      <H5>{person.heading}</H5>
      <DmcDataView data={personData} />
    </>
  )
}

function Name({ person, createCtrActions }) {
  const { t } = useTranslation()
  return (
    <>
      <H6>{t('name', 'Name')}</H6>
      {person?.nameParts?.map((namePart, nameIndex) => {
        return (
          <Row key={namePart.id ? namePart.id : nameIndex}>
            <IconButton
              data-testid="deleteNamePartButton"
              Icon={ContentDelete}
              onClick={() => createCtrActions.deleteNamePart(person.heading, nameIndex)}
              emphasis="high"
              color="red"
              alignY="nextToInput"
            />
            <Select
              options={[
                { value: 'http://gedcomx.org/Given', label: t('name.parts.given', 'Given') },
                { value: 'http://gedcomx.org/Surname', label: t('name.parts.surname', 'Surname') },
                { value: 'http://gedcomx.org/Prefix', label: t('name.parts.prefix', 'Prefix') },
                { value: 'http://gedcomx.org/Suffix', label: t('name.parts.suffix', 'Suffix') },
                { value: 'http://gedcomx.org/Other', label: t('name.parts.other', 'Other') },
              ]}
              value={namePart.type}
              label={t('type', 'Type')}
              onChange={(event) => createCtrActions.setNameType(person.heading, nameIndex, event.target.value)}
            />
            <TextField
              placeholder={namePart.value}
              value={namePart.value}
              label={t('value', 'Value')}
              onChange={(event) => createCtrActions.setNameValue(person.heading, nameIndex, event.target.value)}
            />
          </Row>
        )
      })}
      {person?.nameParts?.length > 4 ? null : (
        <IconButton
          data-testid="addNamePartButton"
          Icon={ContentAdd}
          color="green"
          onClick={() => createCtrActions.addNamePart(person.heading)}
          emphasis="high"
        />
      )}
    </>
  )
}

function Facts({ person, createCtrActions }) {
  const { t } = useTranslation()
  const validFactTypes = [
    { value: '', label: '' },
    {
      label: t('birth', 'Birth'),
      value: 'http://gedcomx.org/Birth',
    },
    {
      label: t('christening', 'Christening'),
      value: 'http://gedcomx.org/Christening',
    },
    {
      label: t('marriage', 'Marriage'),
      value: 'http://gedcomx.org/Marriage',
    },
    {
      label: t('death', 'Death'),
      value: 'http://gedcomx.org/Death',
    },
    {
      label: t('burial', 'Burial'),
      value: 'http://gedcomx.org/Burial',
    },
  ]

  return (
    <>
      <H6>{t('facts', 'Facts')}</H6>
      {Object.keys(person.factsMap).map((conclusionFactId) => {
        const factConclusion = person.factsMap[conclusionFactId]
        return (
          <Row key={conclusionFactId}>
            <InputGroup label={'\xa0'}>
              <IconButton
                Icon={ContentDelete}
                emphasis="high"
                color="red"
                onClick={() => createCtrActions.deleteFact(person.heading, factConclusion.conclusionId)}
              />
            </InputGroup>
            <DmcConclusionFactUIOnly
              type={person.factsComponentsStateMap[factConclusion.conclusionId].type}
              setType={(newType) => {
                createCtrActions.setFactType(person.heading, factConclusion.conclusionId, newType)
              }}
              date={person.factsComponentsStateMap[factConclusion.conclusionId].date}
              setDate={(newDate) => {
                createCtrActions.setFactDateStandards(person.heading, factConclusion.conclusionId, newDate)
              }}
              place={person.factsComponentsStateMap[factConclusion.conclusionId].place}
              setPlace={(newPlace) => {
                createCtrActions.setFactPlaceStandards(person.heading, factConclusion.conclusionId, newPlace)
              }}
              typeOptions={validFactTypes}
              editable
            />
          </Row>
        )
      })}
      <IconButton
        data-testid="createNewFact"
        Icon={ContentAdd}
        emphasis="high"
        color="green"
        name="createNewFact"
        onClick={() => createCtrActions.addFact(person.heading, makeNewEmptyConclusion())}
      />
    </>
  )
}
